import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import { useLocation } from "react-router-dom";
import ProcessStatusContext from "../../context/ProcessStatusContext";
import { useAuthInfo } from "../../context/AuthInfoContext";
import { useToken } from "hooks/useToken";
import { getProcessStatus } from "./services/overviewService";
import { banking } from "utils/api/generated/manucoApi";
import { evaluateCapability } from "utils/capabilities";
import { needsActivation } from "utils/landing";
import { countPendingActions } from "features/pendingActions/services/pendingActionService";
import { countNewAuditAlerts } from "features/audit/services/auditService";

// Haalt de kwartaalvoortgang op en deelt die met dashboard + sidebar-badges.
// Faalt stil (badges/banner verdwijnen dan gewoon).
//
// Eerste lading (#79): op het Overzicht ("/") meteen, want daar is process-status de hoofdinhoud. Op een andere pagina
// pas na INITIAL_DELAY_MS: dan voedt hij enkel de badges, en krijgen de eigen requests van die pagina voorrang op de
// (onder last 2-4 s dure) process-status. Latere verversingen (refresh) gaan altijd meteen.
export const INITIAL_DELAY_MS = 1500;

export default function ProcessStatusProvider({ children }) {
  const { pathname } = useLocation();
  // Enkel de route bij de eerste lading telt; navigeren daarna verandert niets aan het ophalen.
  const initialPathRef = useRef(pathname);
  const isAuthenticated = useIsAuthenticated();
  const authInfo = useAuthInfo();
  const { loading: authLoading, allowed: serverAllowed, waiting, pages, capabilities, access } = authInfo;
  // Issue #23: wie nog op toegang wacht, is wel "allowed" maar heeft geen rol; process-status gaf dan 403 en
  // apiClient stuurde door naar /forbidden. Niets ophalen tot er een rol is. Idem wie enkel een activeerbare rol heeft
  // die nog niet loopt (landing): eerst activeren.
  const allowed = serverAllowed && !waiting && !needsActivation(authInfo);
  // Open profielvoorstellen (badge Bankprofielen): enkel voor wie die pagina ziet (invest/admin, issue #23 R3).
  const seesProfiles = Array.isArray(pages) && pages.includes("bankProfiles");
  const [openProposals, setOpenProposals] = useState(0);
  // Issue #23 (F4): eigen openstaande acties van de assistent (iedereen met de pagina) en nieuwe audit-signalen (enkel
  // voor wie Beheer geactiveerd heeft: die kan ze ook afzetten; zonder activatie zou de aanroep 403 geven).
  const seesPending = Array.isArray(pages) && pages.includes("pendingActions");
  const seesAlerts =
    Array.isArray(pages) && pages.includes("audit") && evaluateCapability({ capabilities, access }, "audit.ack").allowed;
  const [pendingOpen, setPendingOpen] = useState(0);
  const [alertsNew, setAlertsNew] = useState(0);
  const tokenProvider = useToken();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || authLoading || !allowed) return;
    setLoading(true);
    try {
      const data = await getProcessStatus(tokenProvider);
      if (!mounted.current) return;
      setStatus(data);
      setError(null);
    } catch (e) {
      if (!mounted.current) return;
      setError(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [isAuthenticated, authLoading, allowed, tokenProvider]);

  const refreshProposals = useCallback(async () => {
    if (!isAuthenticated || authLoading || !allowed || !seesProfiles) return;
    try {
      // Issue #23 (rechten F3b): de bankprofielen eisen volledig BANK-bereik; wie de pagina wel ziet maar dat bereik niet
      // heeft (bv. enkel BEHEER) krijgt 403. Een badge mag nooit naar /forbidden sturen: stil overslaan.
      const list = await banking.profileProposalList(
        tokenProvider,
        { status: "PROPOSED" },
        { retries: 0, suppressAuthRedirect: true }
      );
      if (mounted.current) setOpenProposals(Array.isArray(list) ? list.length : 0);
    } catch {
      // Stil: zonder badge werkt de sidebar gewoon.
    }
  }, [isAuthenticated, authLoading, allowed, seesProfiles, tokenProvider]);

  // Fail-soft zoals de profielvoorstellen: null (fout) = geen badge, nooit een doorverwijzing.
  const refreshF4Badges = useCallback(async () => {
    if (!isAuthenticated || authLoading || !allowed) return;
    const [pending, alertCount] = await Promise.all([
      seesPending ? countPendingActions(tokenProvider) : Promise.resolve(0),
      seesAlerts ? countNewAuditAlerts(tokenProvider) : Promise.resolve(0),
    ]);
    if (!mounted.current) return;
    setPendingOpen(pending || 0);
    setAlertsNew(alertCount || 0);
  }, [isAuthenticated, authLoading, allowed, seesPending, seesAlerts, tokenProvider]);

  // Eerste lading (pas als de aanmelding rond is) eventueel uitgesteld; daarna (bv. gewijzigde rechten) meteen.
  const ready = isAuthenticated && !authLoading && allowed;
  const startedRef = useRef(false);
  const startDeferred = useCallback((fn) => {
    if (startedRef.current || initialPathRef.current === "/") {
      startedRef.current = true;
      fn();
      return undefined;
    }
    const timer = setTimeout(() => {
      startedRef.current = true;
      fn();
    }, INITIAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => (ready ? startDeferred(refresh) : undefined), [ready, startDeferred, refresh]);
  useEffect(() => (ready ? startDeferred(refreshProposals) : undefined), [ready, startDeferred, refreshProposals]);
  useEffect(() => (ready ? startDeferred(refreshF4Badges) : undefined), [ready, startDeferred, refreshF4Badges]);

  const refreshAll = useCallback(() => {
    refresh();
    refreshProposals();
    refreshF4Badges();
  }, [refresh, refreshProposals, refreshF4Badges]);

  const value = useMemo(() => {
    const totals = status?.totals || {};
    const badges = {
      importPending: totals.importPending > 0 ? String(totals.importPending) : null,
      processingPending: totals.processingPending > 0 ? String(totals.processingPending) : null,
      bankProfileProposals: openProposals > 0 ? String(openProposals) : null,
      // "Te doen": open werklijstitems. process-status levert worklist enkel voor invest/admin
      // en fail-soft null zolang de DB de proc mist: dan geen badge.
      worklistOpen: status?.worklist?.openTotal > 0 ? String(status.worklist.openTotal) : null,
      pendingActionsOpen: pendingOpen > 0 ? String(pendingOpen) : null,
      auditAlertsNew: alertsNew > 0 ? String(alertsNew) : null,
    };
    return { status, loading, error, badges, refresh: refreshAll };
  }, [status, loading, error, refreshAll, openProposals, pendingOpen, alertsNew]);

  return <ProcessStatusContext.Provider value={value}>{children}</ProcessStatusContext.Provider>;
}
